import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GraficoConsumo, GraficoBurbuja, TarjetaResumen } from '../../components/gestion';
import gestionService, {
  DatosConsumo,
  ResumenConsumo,
  DatosEficienciaCombustible,
} from '../../services/gestionService';
import toast from 'react-hot-toast';
import { toISODateSafe } from '../../utils/dateUtils';

type TabConsumos = 'linea-temporal' | 'eficiencia';

export const ConsumosView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Estado para datos
  const [datosConsumo, setDatosConsumo] = useState<DatosConsumo[]>([]);
  const [datosEficiencia, setDatosEficiencia] = useState<DatosEficienciaCombustible[]>([]);
  const [resumen, setResumen] = useState<ResumenConsumo | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabActivo, setTabActivo] = useState<TabConsumos>('linea-temporal');

  // Cargar datos al montar - con rango amplio para poder navegar por meses
  useEffect(() => {
    const cargarDatos = async () => {
      setCargando(true);
      setError(null);

      try {
        // Calcular rango amplio (últimos 90 días)
        const hoy = new Date();
        const hace90Dias = new Date();
        hace90Dias.setDate(hace90Dias.getDate() - 90);

        const fechaInicio = toISODateSafe(hace90Dias);
        const fechaFin = toISODateSafe(hoy);

        // Cargar datos de consumo
        const datosConsumoRes = await gestionService.obtenerDatosConsumo(
          fechaInicio,
          fechaFin
        );
        setDatosConsumo(datosConsumoRes);

        // Cargar resumen
        const resumenRes = await gestionService.obtenerResumenConsumo(
          fechaInicio,
          fechaFin
        );
        setResumen(resumenRes);

        // Cargar datos de eficiencia
        const datosEficienciaRes = await gestionService.obtenerDatosEficienciaCombustible(
          fechaInicio,
          fechaFin
        );
        setDatosEficiencia(datosEficienciaRes);
      } catch (err: any) {
        const errorMsg = err instanceof Error ? err.message : 'Error al cargar datos';
        setError(errorMsg);
        toast.error(errorMsg);
      } finally {
        setCargando(false);
      }
    };

    cargarDatos();
  }, []);

  const handleVolver = () => {
    navigate('/gestion');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <button
          onClick={handleVolver}
          className="flex items-center text-blue-600 hover:text-blue-700 mb-4 font-medium"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {t('management.back_to_management')}
        </button>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">{t('management.consumption')}</h1>
        <p className="text-gray-600">
          {t('management.fuel')}
        </p>
      </div>

      {/* Contenido Principal */}
      <div className="w-full max-w-7xl mx-auto space-y-6">
        {/* Tarjetas de Resumen */}
        {resumen && (
          <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-4">
            <TarjetaResumen
              titulo={t('common.total')}
              valor={resumen.total_consumido}
              color="green"
              icon={
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              }
            />
            <TarjetaResumen
              titulo={t('management.fuel')}
              valor={resumen.insumo_mas_consumido}
              color="blue"
              subtitulo={t('common.total')}
              icon={
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
            />
            <TarjetaResumen
              titulo={t('common.description', 'Cantidad Mayor')}
              valor={resumen.cantidad_mas_consumida}
              color="amber"
              subtitulo={t('common.type', 'Unidades')}
              icon={
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
            />
          </div>
        )}

        {/* Tabs de Gráficos */}
        <div className="w-full space-y-4">
          {/* Controles de Tab */}
          <div className="w-full bg-white rounded-lg shadow" style={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
            <button
              onClick={() => setTabActivo('linea-temporal')}
              style={{
                flex: 1,
                padding: '1rem',
                borderRadius: 'none',
                fontWeight: 500,
                backgroundColor: tabActivo === 'linea-temporal' ? '#2563eb' : 'white',
                color: tabActivo === 'linea-temporal' ? 'white' : '#374151',
                border: 'none',
                cursor: 'pointer',
                transition: 'colors 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                borderBottom: tabActivo === 'linea-temporal' ? '3px solid #2563eb' : 'none',
              }}
            >
              <svg
                style={{ width: '1.25rem', height: '1.25rem', display: 'inline' }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
              {t('management.consumption')}
            </button>
            <button
              onClick={() => setTabActivo('eficiencia')}
              style={{
                flex: 1,
                padding: '1rem',
                borderRadius: 'none',
                fontWeight: 500,
                backgroundColor: tabActivo === 'eficiencia' ? '#2563eb' : 'white',
                color: tabActivo === 'eficiencia' ? 'white' : '#374151',
                border: 'none',
                cursor: 'pointer',
                transition: 'colors 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                borderBottom: tabActivo === 'eficiencia' ? '3px solid #2563eb' : 'none',
              }}
            >
              <svg
                style={{ width: '1.25rem', height: '1.25rem', display: 'inline' }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 12a5 5 0 1110 0A5 5 0 017 12z"
                />
              </svg>
              {t('management.fuel')}
            </button>
          </div>

          {/* Header de contenido */}
          <div className="w-full bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {tabActivo === 'linea-temporal' ? t('management.consumption') : t('management.fuel')}
            </h2>
            <p className="text-gray-600 mb-6">
              {tabActivo === 'linea-temporal'
                ? t('management.consumption')
                : t('management.fuel')}
            </p>

            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                Error: {error}
              </div>
            )}
          </div>

          {/* Gráfico de Línea Temporal */}
          {tabActivo === 'linea-temporal' && (
            <GraficoConsumo datos={datosConsumo} isLoading={cargando} />
          )}

          {/* Gráfico de Eficiencia (Burbuja) */}
          {tabActivo === 'eficiencia' && (
            <GraficoBurbuja datos={datosEficiencia} isLoading={cargando} />
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsumosView;
